import React, { useContext } from 'react';
import { Button } from '@uhc-tempo/components';
import { FormContext } from '../../../context/Form.context';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import styles from '../../../styles/PageStyles.js';
import theme from '../../../styles/PageTheme';
import formatNumberToCurrency from '../../../utils/NumberUtil';
import { getServiceNameToDisplay } from '../../../utils/EstimatePageUtil';

const CopyEstimate = ({ estimate, provider, estimateType, setCopySuccess }) => {
    const classes = styles(theme);
    const { formValues } = useContext(FormContext);
    const { selectedService } = formValues;
    const serviceName = getServiceNameToDisplay(selectedService.value);
    /**
     * function to gather all estimate info, mirrors the content of the table of cost estimate values within the fulfillment letter
     * @param {object} singleEstimate estimate object
     * @param {int} index index of singleEstimate object within full estimate array
     * @returns string of all estimate info concatenated together
     */
    const estimateInfoToCopy = (singleEstimate, index) => {
        let serviceCodeText = 'Service Code Description: ';
        let providerText = 'Provider: ';
        let negotiatedRateText = 'Negotiated Rate/Fee Schedule: ';
        let estPlanPaymentsText = 'Estimated Plan Payments: ';
        let deductibleText = 'Deductible: ';
        let coinsuranceText = 'Coinsurance: ';
        let copayText = 'Copay: ';
        let nonCoveredAmountText = 'Non-Covered Amount: ';
        let estimatedResponsibilityText = 'Estimated Member Responsibility: ';
        let providerEstimatedChargesText = 'Provider Estimated Charges: ';
        let allowedAmountText = 'Allowed Amount: ';

        singleEstimate.claims.map((claim) =>
            claim.procedures.map((procedure, procIndex) => {
                const addComma = procIndex + 1 < claim.procedures.length ? ', ' : '';
                serviceCodeText += `${serviceName}${addComma}`;
                providerText +=
                    estimateType === 'single'
                        ? `${provider.unTranslatedText}${addComma}`
                        : `${provider[index].unTranslatedText}${addComma}`;
                negotiatedRateText += `${formatNumberToCurrency(
                    procedure.costShare.insurancePays + procedure.costShare.outOfPocket,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
                estPlanPaymentsText += `${formatNumberToCurrency(
                    procedure.costShare.insurancePays,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
                deductibleText += `${formatNumberToCurrency(
                    procedure.costShare.appliedToDeductible,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
                copayText += `${formatNumberToCurrency(
                    procedure.costShare.copay,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
                coinsuranceText += `${formatNumberToCurrency(
                    procedure.costShare.coinsurance,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
                nonCoveredAmountText += `${formatNumberToCurrency(
                    procedure.costShare.notCovered,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
                estimatedResponsibilityText += `${formatNumberToCurrency(
                    procedure.costShare.outOfPocket,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
                // TODO: once we have OON examples, confirm these are the correct values
                providerEstimatedChargesText += `${formatNumberToCurrency(
                    procedure.costShare.rate,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
                allowedAmountText += `${formatNumberToCurrency(
                    procedure.costShare.insurancePays,
                    procedure.costShare.outOfPocket
                )}${addComma}`;
            })
        );

        let textArray = [];
        let networkText = '';

        if (
            (estimateType === 'single' &&
                estimate?.claims?.[0]?.procedures?.[0]?.provider.isInNetwork) ||
            estimate[index]?.claims?.[0]?.procedures?.[0]?.provider.isInNetwork
        ) {
            textArray = [
                serviceCodeText,
                providerText,
                negotiatedRateText,
                estPlanPaymentsText,
                deductibleText,
                copayText,
                coinsuranceText,
                nonCoveredAmountText,
                estimatedResponsibilityText,
            ];
            networkText = 'Network Cost Estimate: ';
        } else {
            textArray = [
                serviceCodeText,
                providerEstimatedChargesText,
                allowedAmountText,
                deductibleText,
                copayText,
                coinsuranceText,
                nonCoveredAmountText,
                estimatedResponsibilityText,
            ];
            networkText = 'Out-of-Network Cost Estimate: ';
        }

        return networkText + textArray.join(', ');
    };

    /**
     * calls functions to gather and format all content to copy, then writes that content to clipbord
     * calls props.setCopySuccess to display a success snackbar upon succesful completion
     */
    const copyToClipboard = () => {
        let textToCopy = '';

        if (estimateType === 'single') {
            textToCopy += `[${estimateInfoToCopy(estimate, 0)}]`;
        } else if (estimateType === 'multiple') {
            estimate.map((singleEstimate, index) => {
                const addComma = index + 1 < estimate.length ? ', ' : '';
                textToCopy += `[${estimateInfoToCopy(singleEstimate, index)}]${addComma}`;
            });
        }

        navigator.clipboard.writeText(textToCopy);

        setCopySuccess();
    };

    return (
        <Button
            buttonType="secondary-one"
            onClick={(e) => {
                e.preventDefault();
                copyToClipboard();
            }}
            style={{
                width: '100%',
            }}
        >
            <ContentCopyIcon className={'.tds-button--secondary-one'} sx={{ mr: 0.5 }} />
            <span style={classes.printEstimateText}>Copy</span>
        </Button>
    );
};

export default CopyEstimate;
